import X from '../x.js';
import '../x-router/index.js';
import '../x-menu-bar/index.js';
import '../x-dialog/index.js';
import '../x-add/index.js';
import '../x-info/index.js';
import '../x-icon/index.js';

import commonCSS from '../../../css/common.js';

class XApp extends X {
  static get properties() {
    return {
      items: {
        type: Array
      },
      route: {
        type: String
      }
    };
  }

  constructor() {
    super();

    this._routes = {
      home: {
        pattern: '^/$',
        elementName: 'x-list',
        triggers: ['items']
      },
      timer: {
        pattern: '^/item/(.*)$',
        elementName: 'x-timer',
        params: ['_id'],
        triggers: ['items']
      }
    };

    this.route = null;

    const savedItems = localStorage.getItem('items');
    this.items = savedItems ? JSON.parse(savedItems) : [];

    this.viewState = {};
  }

  render(target) {
    target.innerHTML = `
      <style>
        ${commonCSS}

        :host {
          position: absolute;
          top: 0;
          right: 0;
          left: 0;
          overflow: hidden;
          height: 100vh;
        }

        #main {
          position: relative;
        }

        .view {
          position: absolute;
          top: 0;
          right: 0;
          left: 0;
          overflow: hidden;
          height: 100vh;
        }

        x-menu-bar {
          position: fixed;
          overflow: hidden;
        }

        #menuNav {
          bottom: 2rem;
          left: 2rem;

          transition: transform 0.3s ease-in-out 0s;
          transform: translateY(calc(100% + 2rem));
        }

        #menuNav.show {
          transform: translateY(0);
        }

        #menuAddInfo {
          bottom: 2rem;
          right: 2rem;
        }

        #menuAddInfo #add,
        #menuAddInfo #edit {
          transition: opacity 0.3s ease-in-out 0s, transform 0.3s ease-in-out 0s;
        }

        #menuAddInfo #edit {
          position: absolute;
        }

        #menuAddInfo #add,
        #menuAddInfo.edit #edit {
          opacity: 1;
          z-index: 1;
          transform: rotate(0deg);
        }

        #menuAddInfo #edit,
        #menuAddInfo.edit #add {
          opacity: 0;
          z-index: 0;
          transform: rotate(180deg);
        }

        #menuAddInfo.edit #add {
         transform: rotate(-180deg);
        }

        x-add, x-info {
          background-color: white;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
      </style>

      <x-router id="router"></x-router>
      <main id="main"></main>

      <x-menu-bar id="menuNav">
        <a href="/">
          <x-icon name="reorder" size="22"></x-icon>
        </a>
      </x-menu-bar>

      <x-menu-bar id="menuAddInfo">
        <button id="add">
          <x-icon name="add" size="28"></x-icon>
        </button>
        <button id="edit">
          <x-icon name="mode-edit" size="20"></x-icon>
        </button>
        <button id="info">
          <x-icon name="info" size="34"></x-icon>
        </button>
      </x-menu-bar>

      <x-dialog id="addDialog">
        <x-add></x-add>
      </x-dialog>

      <x-dialog id="infoDialog">
        <x-info></x-info>
      </x-dialog>
    `;
  }

  setup(target, triggers) {
    this.on(this, 'x-item-add', this.onItemAdd);
    this.on(this, 'x-item-delete', this.onItemDelete);

    this.on(this.$.add, 'click', this.openAdd);
    this.on(this.$.edit, 'click', this.openEdit);
    this.on(this.$.info, 'click', this.openInfo);

    this.on(this.$.addDialog, 'x-close', this.closeAdd);

    this.on(this.$.router, 'x-update-route', event => {
      const { route, params } = event.detail;
      this.route = route;
      this.routeParams = params;
    });

    Promise.resolve().then(() => {
      this.$.router.routes = this._routes;
      this.update(target, triggers);
    });
  }

  onItemAdd(event) {
    const { _id, name, when, start, color } = event.detail;

    this.items = this.items.slice();

    const existingIndex = this.items.findIndex(item => item._id === _id);
    if (existingIndex > -1) {
      this.items[existingIndex] = {
        ...this.items[existingIndex],
        name,
        when,
        start,
        color
      };
    } else {
      this.items.push({
        _id,
        name,
        when,
        start,
        color
      });
    }

    this.closeAdd();
  }

  onItemDelete(event) {
    const { _id } = event.detail;

    const index = this.items.findIndex(existing => existing._id === _id);

    this.items = this.items.slice();
    this.items.splice(index, 1);

    this.closeAdd();

    this.$.router.checkPath('/');
  }

  updated(triggers) {
    if ('items' in triggers) {
      localStorage.setItem('items', JSON.stringify(this.items));
    }

    return true;
  }

  async update(target, triggers) {
    if (this.route && 'route' in triggers) {
      if (triggers.route) {
        const lastView = target.querySelector(`#view-${triggers.route}`);
        this.viewState[triggers.route] = lastView.saveState
          ? lastView.saveState()
          : {};
      }

      if (!triggers.route && this.route === 'timer') {
        this.$.menuNav.classList.add('show');
        this.$.menuAddInfo.classList.add('edit');
      }

      const { elementName } = this._routes[this.route];

      switch (elementName) {
        case 'x-list':
          await import('../x-list/index.js');
          break;
        case 'x-timer':
          await import('../x-timer/index.js');
          break;
      }

      const el = document.createElement(elementName);
      el.className = 'view';
      el.id = `view-${this.route}`;
      this.$.main.appendChild(el);
      if (el.restoreState) {
        el.restoreState(this.viewState[this.route]);
      }

      this.transition(target, triggers.route, this.route);

      switch (this.route) {
        case 'home':
          this.$.menuNav.classList.remove('show');
          this.$.menuAddInfo.classList.remove('edit');
          break;
        case 'timer':
          this.$.menuNav.classList.add('show');
          this.$.menuAddInfo.classList.add('edit');
          break;
      }
    }

    const view = target.querySelector(`#view-${this.route}`);
    if (view) {
      switch (this.route) {
        case 'home':
          if ('route' in triggers || 'items' in triggers) {
            view.items = this.items;
          }
          break;
        case 'timer':
          if ('route' in triggers || 'items' in triggers) {
            const item = this.items.find(
              item => item._id === this.routeParams._id
            );
            Object.keys(item).forEach(propName => {
              view[propName] = item[propName];
            });
          }
          break;
      }
    }
  }

  openAdd() {
    this.$.addDialog.open();
  }

  closeAdd() {
    if (this.$.addDialog.isOpen) {
      this.$.addDialog.close();
    }

    const add = this.$.addDialog.querySelector('x-add');
    add.clear();
  }

  openEdit() {
    const add = this.$.addDialog.querySelector('x-add');

    const item = this.items.find(item => item._id === this.routeParams._id);
    Object.keys(item).forEach(propName => {
      add[propName] = item[propName];
    });

    this.openAdd();
  }

  openInfo() {
    this.$.infoDialog.open();
  }

  closeInfo() {
    this.$.infoDialog.close();
  }

  async transition(target, from, to) {
    if (from) {
      const fromEl = target.querySelector(`#view-${from}`);
      const toEl = target.querySelector(`#view-${to}`);

      if (!fromEl.animate) {
        fromEl.remove();
        return;
      }

      let direction = 'r2l';
      if (to === 'home') {
        direction = 'l2r';
      }

      const toAnim = toEl.animate(
        [
          { transform: `translateX(${direction === 'r2l' ? 100 : -100}%)` },
          { transform: `translateX(0%)` }
        ],
        {
          duration: 300,
          iterations: 1,
          easing: 'ease-in-out'
        }
      );
      toAnim.onfinish = () => {
        fromEl.remove();
      };

      fromEl.animate(
        [
          { transform: `translateX(0%)` },
          { transform: `translateX(${direction === 'r2l' ? -100 : 100}%)` }
        ],
        {
          duration: 500,
          iterations: 1,
          easing: 'ease-in-out'
        }
      );
    }
  }
}

customElements.define('x-app', XApp);
